import { MarkerClusterer } from '@googlemaps/markerclusterer';
import { geocodeAddress } from './geocodeAddress';

window.addEventListener(
  'DOMContentLoaded',
  async () => {
    if (!location.href.includes('/pages')) return;
    if (!document.querySelector('.store_map__wrapper')) return;

    // ---- Constants / State ----------------------------------------------------
    const DEFAULT_CENTER = { lat: 56.20279, lng: 10.25049 };
    const DEFAULT_ZOOM = 7.5;
    const ZOOM_SELECTED = 15;
    const ZOOM_USER = 10;
    const CACHE_VERSION = '5';

    const state = {
      map: null,
      markers: [],
      infoWindows: [],
      allLocations: [],
      filteredLocations: [],
      selectedStoreName: '',
      nameIndex: new Map(),
      typingTimer: null,
      searchDelayMs: 500,
    };

    // ---- Utilities ------------------------------------------------------------
    const $ = (sel, root = document) => root.querySelector(sel);
    const $$ = (sel, root = document) => Array.from(root.querySelectorAll(sel));

    const safeParseFloat = (val) => {
      if (val == null) return null;
      if (typeof val === 'string') val = val.replace(',', '.');
      const n = parseFloat(val);
      return Number.isFinite(n) ? n : null;
    };

    const normalizeLatLng = (obj) => {
      const lat = safeParseFloat(obj.lat ?? obj.latitude);
      const lng = safeParseFloat(obj.lng ?? obj.longitude);
      if (lat == null || lng == null) return null;
      return { lat, lng };
    };

    const cleanUpBrokenCharacters = (text) => {
      if (!text) return '';
      return text.replaceAll('Ã˜', 'Ø').replaceAll('Ã¸', 'ø').replaceAll('Ã¥', 'å').replaceAll('Ã¦', 'æ').replaceAll('Â', '').replaceAll('Ã©', 'é');
    };

    const handleizeString = (input) => {
      if (typeof input !== 'string') throw new Error('handleizeString expects string');
      return input.trim().toLowerCase().replace(/\s+/g, '-');
    };

    const deg2rad = (deg) => deg * (Math.PI / 180);
    const haversineKm = (lat1, lon1, lat2, lon2) => {
      const R = 6371;
      const dLat = deg2rad(lat2 - lat1);
      const dLon = deg2rad(lon2 - lon1);
      const a = Math.sin(dLat / 2) ** 2 + Math.cos(deg2rad(lat1)) * Math.cos(deg2rad(lat2)) * Math.sin(dLon / 2) ** 2;
      return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
    };

    const distanceOrNull = (loc, user) => {
      const lat = safeParseFloat(loc?.address?.latitude);
      const lng = safeParseFloat(loc?.address?.longitude);
      if (![lat, lng, user?.lat, user?.lng].every(Number.isFinite)) return null;
      return +haversineKm(lat, lng, user.lat, user.lng).toFixed(2);
    };

    const closeAllInfoWindows = () => state.infoWindows.forEach((iw) => iw.close());

    const removePOIFromMap = () => {
      state.map?.setOptions({
        styles: [
          { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
          { featureType: 'transit', elementType: 'labels.icon', stylers: [{ visibility: 'off' }] },
        ],
      });
    };

    // ---- Data loading / caching ----------------------------------------------
    async function loadLocations(mapData) {
      if (sessionStorage.getItem('StoreLocations') && sessionStorage.getItem('StoreLocationsVersion') === CACHE_VERSION) {
        return JSON.parse(sessionStorage.getItem('StoreLocations'));
      }

      const storesResp = await fetch('https://intersportapi.netlify.app/.netlify/functions/getStores');
      const apiLocations = await storesResp.json();
      const enabled = apiLocations.filter((l) => l.warehouse !== 'true');

      const merged = [...(mapData.locations || []), ...enabled];

      // Fill in missing lat/lng using geocode
      for (const loc of merged) {
        const lat = safeParseFloat(loc?.address?.latitude);
        const lng = safeParseFloat(loc?.address?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) {
          const geo = await geocodeAddress(loc?.address?.address1 || '');
          loc.address.latitude = geo?.latitude;
          loc.address.longitude = geo?.longitude;
        }
      }

      sessionStorage.setItem('StoreLocations', JSON.stringify(merged));
      sessionStorage.setItem('StoreLocationsVersion', CACHE_VERSION);
      return merged;
    }

    // ---- Map init / markers / clusters ---------------------------------------
    function clearMarkers() {
      state.markers.forEach((m) => m.setMap(null));
      state.markers = [];
    }

    function addMarker(position, icon, location = null) {
      const marker = new google.maps.Marker({ position, map: state.map, icon });
      if (location) attachInfoWindow(marker, location);
      state.markers.push(marker);
      return marker;
    }

    function attachInfoWindow(marker, location) {
      const name = cleanUpBrokenCharacters(location?.name) || '';
      const road = cleanUpBrokenCharacters(location?.address?.address1) || '';
      const zip = cleanUpBrokenCharacters(location?.address?.zip) || '';
      const city = cleanUpBrokenCharacters(location?.address?.city) || '';
      const addr = `${road}, ${zip} ${city}`.trim();

      const iw = new google.maps.InfoWindow({
        content: `<div class="map-card"><b class="map-header">${name}</b><br />${addr}<br /></div>`,
        maxWidth: 300,
      });
      state.infoWindows.push(iw);

      marker.addListener('click', () => {
        // unselect all cards
        $$('.store_card__wrapper').forEach((c) => (c.dataset.selected = 'false'));

        // toggle open/close (but make sure only one open)
        if (iw.getMap() && iw.anchor === marker) iw.close();
        else {
          closeAllInfoWindows();
          iw.open(state.map, marker);
        }

        // After content renders, sync selection and possibly open details
        setTimeout(() => selectFromMarker(name), 250);
      });
    }

    function setupClusters(clusterIcon) {
      new MarkerClusterer({
        map: state.map,
        markers: state.markers,
        renderer: {
          render: ({ count, position }) =>
            new google.maps.Marker({
              position,
              icon: clusterIcon,
              label: { text: String(count), color: 'white', fontSize: '10px' },
              zIndex: Number(google.maps.Marker.MAX_ZINDEX) + count,
            }),
        },
      });
    }

    function rebuildNameIndex() {
      state.nameIndex.clear();
      state.filteredLocations.forEach((loc, i) => {
        if (!loc) return;
        const key = cleanUpBrokenCharacters(loc.name || '').toUpperCase();
        if (key) state.nameIndex.set(key, i);
      });
    }

    function indexOfSelected() {
      if (!state.selectedStoreName) return -1;
      return state.nameIndex.get(cleanUpBrokenCharacters(state.selectedStoreName).toUpperCase()) ?? -1;
    }

    function placeMarkers(markerIcon, markerIconSelected, clusterIcon, selectedByMarker = false) {
      clearMarkers();
      const selectedIdx = indexOfSelected();

      state.filteredLocations.forEach((loc, i) => {
        if (!loc) return;
        const lat = safeParseFloat(loc?.address?.latitude);
        const lng = safeParseFloat(loc?.address?.longitude);
        if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

        const pos = new google.maps.LatLng(lat, lng);
        const icon = i === selectedIdx ? markerIconSelected || markerIcon : markerIcon;
        addMarker(pos, icon, loc);
      });

      setupClusters(clusterIcon);

      // If selected via marker click, reflect selection in list + open details view
      if (selectedByMarker && selectedIdx > -1) {
        const selectedName = cleanUpBrokenCharacters(state.filteredLocations[selectedIdx]?.name || '');
        const cardId = handleizeString(selectedName);
        $$('.store_card__wrapper').forEach((c) => c.setAttribute('data-selected', 'false'));
        const target = $(`.store_card__wrapper[store-card-id="${cardId}"]`);
        if (target) target.setAttribute('data-selected', 'true');
        openStorePage(selectedName);
      }
    }

    async function initMap(center, zoom) {
      const centerNorm = normalizeLatLng(center) ?? DEFAULT_CENTER;

      if (!state.allLocations.length) {
        const mapData = JSON.parse($('#map-data')?.textContent || '{}');
        state.settings = {
          markerImage: mapData?.settings?.marker_image || '',
          clusterMarkerImage: mapData?.settings?.cluster_image || '',
          userMarkerImage: mapData?.settings?.user_image || '',
          markerImageSelected: mapData?.settings?.marker_image_selected || '',
          storeT: mapData?.settings?.store_single || 'store',
          storesT: mapData?.settings?.stores_plural || 'stores',
        };
        state.allLocations = await loadLocations(mapData);
        // Normalize number types once
        state.allLocations.forEach((loc) => {
          if (!loc?.address) return;
          loc.address.latitude = safeParseFloat(loc.address.latitude);
          loc.address.longitude = safeParseFloat(loc.address.longitude);
        });
        state.filteredLocations = state.allLocations.slice();
        rebuildNameIndex();
      }

      state.map = new google.maps.Map($('#map'), { center: centerNorm, zoom, mapTypeId: 'terrain' });
      removePOIFromMap();

      placeMarkers(state.settings.markerImage, state.settings.markerImageSelected, state.settings.clusterMarkerImage);
    }

    // ---- Cards / rendering ----------------------------------------------------
    function addDirectionsLink(node, storeName) {
      const link = `https://www.google.com/maps/dir//${encodeURIComponent(storeName)}`;
      node.querySelector('.directions a')?.setAttribute('href', link);
    }

    function addPhoneLink(node, phoneRaw) {
      if (!phoneRaw) {
        node.querySelector('.call')?.classList.add('hidden');
        return;
      }
      let phone = String(phoneRaw);
      if (window.innerWidth >= 750) {
        if (phone.startsWith('+45')) phone = phone.slice(3);
        const formatted = phone.replace(/(\d{2})/g, '$1 ').trim();
        node.querySelector('.call a').textContent = `Tlf: ${formatted}`;
        node.querySelector('.call a').removeAttribute('href');
      } else {
        node.querySelector('.call a').setAttribute('href', `tel:${phone}`);
      }
    }

    function getOpeningHours(loc) {
      return cleanUpBrokenCharacters(loc?.openingHours || '');
    }

    function getUserLocation(cbOK, cbErr) {
      if (!navigator.geolocation) return cbErr?.();
      navigator.geolocation.getCurrentPosition(
        (pos) => cbOK?.({ lat: pos.coords.latitude, lng: pos.coords.longitude }),
        () => cbErr?.(),
        { enableHighAccuracy: true, maximumAge: 60_000, timeout: 10_000 }
      );
    }

    function setStoreSoMeLinks(node, loc) {
      const socialLinks = node.querySelector('.social_links');
      if (!socialLinks) return;
      const igLink = socialLinks.querySelector('.instagram_link');
      const fbLink = socialLinks.querySelector('.facebook_link');
      const mfs = Array.isArray(loc?.metafields) ? loc.metafields : [];
      const ig = mfs.find((m) => m.key === 'instagram_link')?.value || '';
      const fb = mfs.find((m) => m.key === 'facebook_link')?.value || '';
      if (ig) igLink?.setAttribute('href', ig);
      if (fb) fbLink?.setAttribute('href', fb);
    }

    function unhideProvidedSocialMediaLinks(node) {
      let shown = false;
      const socialLinks = node.querySelector('.social_links');
      if (!socialLinks) return;
      const igLink = socialLinks.querySelector('.instagram_link');
      const fbLink = socialLinks.querySelector('.facebook_link');
      if (igLink && igLink.getAttribute('href') && igLink.getAttribute('href') !== '#') {
        igLink.classList.remove('hidden');
        shown = true;
      }
      if (fbLink && fbLink.getAttribute('href') && fbLink.getAttribute('href') !== '#') {
        fbLink.classList.remove('hidden');
        shown = true;
      }
      socialLinks.classList[shown ? 'remove' : 'add']('hidden');
    }

    function servicesFor(loc) {
      const mfs = Array.isArray(loc?.metafields) ? loc.metafields : [];
      const svc = mfs.find((m) => m.key === 'services')?.value;
      try {
        return svc ? JSON.parse(svc) : [];
      } catch {
        return [];
      }
    }

    function showServices(services) {
      if (!services.length) {
        $('.selected_store_page__services')?.classList.add('hidden');
        return;
      }
      const wrap = $('.selected_store_page__services__wrapper');
      $$('.service_card', wrap).forEach((c) => c.classList.add('hidden'));
      services.forEach((name) => {
        const wanted = (name || '').toUpperCase();
        $$('.service_card', wrap).forEach((c) => {
          if (c.querySelector('.service_title')?.innerText.toUpperCase() === wanted) {
            c.classList.remove('hidden');
          }
        });
      });
      $('.selected_store_page__services')?.classList.remove('hidden');
    }

    function buildLocationCards(userPos = null) {
      const container = $('.store_cards');
      if (!container) return;
      container.innerHTML = '';

      state.filteredLocations.forEach((loc) => {
        if (!loc || cleanUpBrokenCharacters(loc.name) === 'Shop location') return;

        const tpl = $('#store_card__template');
        if (!tpl) return;
        const clone = tpl.content.cloneNode(true);

        const name = cleanUpBrokenCharacters(loc.name);
        const road = cleanUpBrokenCharacters(loc.address?.address1);
        const zip = cleanUpBrokenCharacters(loc.address?.zip ?? loc.address?.postal_code ?? '');
        const city = cleanUpBrokenCharacters(loc.address?.city);
        const addr = `${road}, ${zip} ${city}`.trim();

        if (name) clone.querySelector('.store_card__wrapper')?.setAttribute('store-card-id', handleizeString(name));
        clone.querySelector('.store_card__shop_name').innerText = name || '';
        clone.querySelector('.store_card__shop_address').innerText = addr || '';
        clone.querySelector('.store_card__shop_info').innerText = getOpeningHours(loc) || '';

        const dist = userPos ? distanceOrNull(loc, userPos) : null;
        const distWrap = clone.querySelector('.store_card__shop_distance');
        if (dist && distWrap) {
          distWrap.classList.remove('hidden');
          distWrap.querySelector('span').innerText = String(dist);
        } else {
          distWrap?.classList.add('hidden');
        }

        setStoreSoMeLinks(clone, loc);
        if (name) addDirectionsLink(clone, name);
        if (loc.address?.phone) addPhoneLink(clone, loc.address?.phone);

        clone.querySelector('.open_store_page')?.addEventListener('click', () => openStorePage(name));

        container.appendChild(clone);

        if (state.selectedStoreName && name.toUpperCase() === state.selectedStoreName.toUpperCase()) {
          container.lastElementChild?.setAttribute('data-selected', 'true');
        }
      });

      // click to select
      $$('.store_card__wrapper').forEach((card) => {
        card.addEventListener('click', onCardSelect);
      });

      updateAvailableStoreCount();
    }

    function updateAvailableStoreCount() {
      const count = state.filteredLocations.filter((l) => l && cleanUpBrokenCharacters(l.name) !== 'Shop location').length;
      const label = count === 1 ? state.settings.storeT : state.settings.storesT;
      $('.filter_and_search_stores__available_count span').innerText = `${count} ${label}`;
    }

    // ---- Interaction handlers -------------------------------------------------
    function onCardSelect(e) {
      const card = e.currentTarget;
      $$('.store_card__wrapper').forEach((c) => (c.dataset.selected = 'false'));
      card.dataset.selected = 'true';

      state.selectedStoreName = card.querySelector('.store_card__shop_name')?.innerText || '';
      const idx = indexOfSelected();
      if (idx < 0) return;

      const loc = state.filteredLocations[idx];
      const lat = safeParseFloat(loc?.address?.latitude);
      const lng = safeParseFloat(loc?.address?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      state.map.panTo({ lat, lng });
      state.map.setZoom(ZOOM_SELECTED);

      // Mobile overlay
      if (window.innerWidth < 750) {
        const holder = $('.selected_store_map_card');
        if (!holder) return;
        holder.innerHTML = '';
        const clone = card.cloneNode(true);
        holder.appendChild(clone);
        $('.selected_store_map_card .icon-close')?.addEventListener('click', () => {
          holder.classList.add('hidden');
          holder.innerHTML = '';
        });
        holder.classList.remove('hidden');
        holder.querySelector('[data-buttons]')?.classList.add('flex-wrap', 'overflow-x-auto');
      }
    }

    function selectFromMarker(name) {
      state.selectedStoreName = name;
      $$('.store_card__wrapper').forEach((c) => (c.dataset.selected = 'false'));
      $$('.store_card__wrapper').forEach((c) => {
        if (c.querySelector('.store_card__shop_name')?.innerText.toUpperCase() === cleanUpBrokenCharacters(name).toUpperCase()) {
          c.dataset.selected = 'true';
        }
      });

      const idx = indexOfSelected();
      if (idx < 0) return;
      const loc = state.filteredLocations[idx];
      const lat = safeParseFloat(loc?.address?.latitude);
      const lng = safeParseFloat(loc?.address?.longitude);
      if (!Number.isFinite(lat) || !Number.isFinite(lng)) return;

      state.map.panTo({ lat, lng });
      state.map.setZoom(ZOOM_SELECTED);

      if (window.innerWidth < 750) {
        const holder = $('.selected_store_map_card');
        holder.innerHTML = '';
        const card = $$('.store_card__wrapper').find((c) => c.querySelector('.store_card__shop_name')?.innerText.toUpperCase() === cleanUpBrokenCharacters(name).toUpperCase());
        if (card) {
          const clone = card.cloneNode(true);
          holder.appendChild(clone);
          $('.selected_store_map_card .icon-close')?.addEventListener('click', () => {
            holder.classList.add('hidden');
            holder.innerHTML = '';
          });
          $('.selected_store_map_card .open_store_page')?.addEventListener('click', () => openStorePage(name));
          holder.classList.remove('hidden');
          holder.querySelector('[data-buttons]')?.classList.add('overflow-x-auto', 'pb-1');
        }
      }

      if (!$('.selected_store_page')?.classList.contains('hidden')) {
        openStorePage(name);
      }
    }

    function openStorePage(name) {
      $('.filter_stores')?.classList.add('hidden');

      // reset
      $('.selected_store_page__card .store_card__wrapper')?.remove();
      $$('.selected_store_page__services__wrapper .service_card').forEach((c) => c.classList.add('hidden'));
      $('.selected_store_page__services')?.classList.add('hidden');

      $('#map')?.classList.remove('hidden');
      $('.store_cards')?.classList.add('hidden');
      $('.filter_and_search_stores__available_count')?.classList.add('hidden');
      $("button[name='view list']")?.classList.add('hidden');
      $("button[name='view map']")?.classList.add('hidden');
      const card = $$('.store_cards .store_card__wrapper').find((c) => c.querySelector('.store_card__shop_name')?.innerText.toUpperCase() === cleanUpBrokenCharacters(name).toUpperCase());
      if (card) {
        const clone = card.cloneNode(true);
        clone.querySelector('.open_store_page')?.classList.add('hidden');
        unhideProvidedSocialMediaLinks(clone);
        $('.selected_store_page__card')?.appendChild(clone);
        $('.selected_store_page .back_to_search')?.addEventListener('click', () => {
          closeStorePage();
          $("button[name='view list']")?.classList.add('hidden');
          $("button[name='view map']")?.classList.remove('hidden');
        });
      }

      // services
      const idx = indexOfSelected();
      const loc = idx > -1 ? state.filteredLocations[idx] : null;
      showServices(servicesFor(loc));

      $('.store_map__wrapper')?.classList.add('store_page');
      $('.selected_store_page')?.classList.remove('hidden');
    }

    function closeStorePage() {
      $('.selected_store_page__card .store_card__wrapper')?.remove();
      $('.selected_store_page')?.classList.add('hidden');
      $$('.selected_store_page__services__wrapper .service_card').forEach((c) => c.classList.add('hidden'));
      $('.store_map__wrapper')?.classList.remove('store_page');
      $('.selected_store_page__services')?.classList.add('hidden');

      $('#map')?.classList.add('hidden');
      $('.store_cards')?.classList.remove('hidden');
      $('.filter_and_search_stores__available_count')?.classList.remove('hidden');
      $("button[name='view list']")?.classList.remove('hidden');
      $("button[name='view map']")?.classList.add('hidden');
      $('.filter_stores')?.classList.remove('hidden');
    }

    // ---- Search / filter ------------------------------------------------------
    function applySearch(term) {
      const t = (term || '').trim();
      if (!t) {
        state.filteredLocations = state.allLocations.slice();
      } else if (!Number.isNaN(Number(t))) {
        // ZIP starts with
        state.filteredLocations = state.allLocations.filter((l) => {
          if (!l || cleanUpBrokenCharacters(l.name) === 'Shop location') return false;
          const zip = String(l?.address?.zip || '').trim();
          return !!zip && zip.startsWith(t);
        });
      } else {
        const U = t.toUpperCase();
        state.filteredLocations = state.allLocations.filter((l) => {
          if (!l || cleanUpBrokenCharacters(l.name) === 'Shop location') return false;
          const hay = `${l?.address?.address1 || ''} ${l?.address?.city || ''} ${l?.name || ''}`.toUpperCase();
          return hay.includes(U);
        });
      }

      rebuildNameIndex();

      // Rebuild list and markers
      $('.store_cards').innerHTML = '';
      getUserLocation(
        (pos) => {
          buildLocationCards(pos);
        },
        () => {
          buildLocationCards(null);
        }
      );

      clearMarkers();
      placeMarkers(state.settings.markerImage, state.settings.markerImageSelected, state.settings.clusterMarkerImage);
    }

    // ---- UI bootstrapping -----------------------------------------------------
    async function boot() {
      // parse config up-front to get images/labels
      const mapData = JSON.parse($('#map-data')?.textContent || '{}');
      state.settings = {
        markerImage: mapData?.settings?.marker_image || '',
        clusterMarkerImage: mapData?.settings?.cluster_image || '',
        userMarkerImage: mapData?.settings?.user_image || '',
        markerImageSelected: mapData?.settings?.marker_image_selected || '',
        storeT: mapData?.settings?.store_single || 'store',
        storesT: mapData?.settings?.stores_plural || 'stores',
      };

      await initMap(DEFAULT_CENTER, DEFAULT_ZOOM);

      // Initial cards
      getUserLocation(
        (pos) => {
          buildLocationCards(pos);
          $('.icon-localize')?.classList.remove('hidden');
        },
        () => buildLocationCards(null)
      );

      // Search input
      const searchInput = $('.filter_and_search_stores__header input');
      if (searchInput) {
        searchInput.addEventListener('input', () => {
          clearTimeout(state.typingTimer);
          state.typingTimer = setTimeout(() => applySearch(searchInput.value), state.searchDelayMs);
        });
      }

      // Localize button
      $('.icon-localize')?.addEventListener('click', () => {
        navigator.permissions
          ?.query?.({ name: 'geolocation' })
          .then((perm) => {
            if (perm?.state === 'granted') {
              navigator.geolocation.getCurrentPosition(
                async (position) => {
                  const latLng = { lat: position.coords.latitude, lng: position.coords.longitude };
                  await initMap(latLng, ZOOM_USER); // rebuild first
                  addMarker(latLng, state.settings.userMarkerImage); // then add user marker
                },
                (err) => console.error('Geolocation error:', err)
              );
            } else {
              // If not granted, try anyway (browser will prompt)
              getUserLocation(
                async (pos) => {
                  await initMap(pos, ZOOM_USER);
                  addMarker(pos, state.settings.userMarkerImage);
                },
                () => {}
              );
            }
          })
          .catch(() => {
            // Fallback if Permissions API isn’t available
            getUserLocation(
              async (pos) => {
                await initMap(pos, ZOOM_USER);
                addMarker(pos, state.settings.userMarkerImage);
              },
              () => {}
            );
          });
      });

      // List/map toggle
      $("button[name='view list']")?.addEventListener('click', toggleListMap);
      $("button[name='view map']")?.addEventListener('click', toggleListMap);
    }

    function toggleListMap() {
      $('#map')?.classList.toggle('hidden');
      $('.store_cards')?.classList.toggle('hidden');
      $('.filter_and_search_stores__available_count')?.classList.toggle('hidden');
      $("button[name='view list']")?.classList.toggle('hidden');
      $("button[name='view map']")?.classList.toggle('hidden');
    }

    // Go!
    try {
      await boot();
    } catch (e) {
      console.error('Map boot failed', e);
    }
  },
  { once: true }
);
